import { isAppleDevice } from "@pandas/utils";
import { useEffect } from "react";

let rafId: number | null = null;
let styleElement: HTMLStyleElement | null = null;

function getStyleElement(): HTMLStyleElement {
	if (!styleElement) {
		const existing = document.getElementById("viewport-height-polyfill") as HTMLStyleElement;
		if (existing) {
			styleElement = existing;
		} else {
			styleElement = document.createElement("style");
			styleElement.id = "viewport-height-polyfill";
			document.head.appendChild(styleElement);
		}
	}
	return styleElement;
}

function updateViewport(): void {
	if (rafId !== null) cancelAnimationFrame(rafId);

	rafId = requestAnimationFrame(() => {
		const dvhValue = window.visualViewport ? window.visualViewport.height * 0.01 : window.innerHeight * 0.01;

		// Width counterpart of --1dvh. iPadOS WKWebView can leave the layout width pinned to a stale
		// `100vw`/`100dvw` after an app-switch (height recovers, width does not), so we publish a
		// JS-measured pixel value that the `.w-[100dvw]` fallback consumes — mirroring the height pattern.
		const dvwValue = window.visualViewport ? window.visualViewport.width * 0.01 : window.innerWidth * 0.01;

		const svhValue = document.documentElement.clientHeight * 0.01;

		let lvhValue: number;
		if (window.screen?.height) {
			lvhValue = window.screen.height * 0.01;
		} else if (document.body) {
			const fixed = document.createElement("div");
			fixed.style.cssText = "width:1px;height:100vh;position:fixed;left:0;top:0;visibility:hidden;pointer-events:none";
			document.body.appendChild(fixed);
			lvhValue = fixed.clientHeight * 0.01;
			fixed.remove();
		} else {
			lvhValue = dvhValue;
		}

		getStyleElement().textContent = `
      :root {
        --1dvh: ${dvhValue}px;
        --1dvw: ${dvwValue}px;
        --1svh: ${svhValue}px;
        --1lvh: ${lvhValue}px;
        }
      `;

		// Force a synchronous reflow so the WebView re-resolves layout against the refreshed values.
		void document.documentElement.offsetWidth;

		rafId = null;
	});
}

// Briefly mutate then restore the <meta viewport> descriptor to force WebKit to recompute a layout
// viewport that iPadOS can leave stale after an app-switch (renders the WebView at ~half width).
function reassertViewportMeta(): void {
	const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
	const content = meta?.getAttribute("content");
	if (!meta || !content) return;

	meta.setAttribute("content", "width=device-width");
	requestAnimationFrame(() => meta.setAttribute("content", content));
}

function _browserSupportsNewVhUnits(): boolean {
	return (
		window &&
		"CSS" in window &&
		CSS.supports("height: 100dvh") &&
		CSS.supports("height: 100lvh") &&
		CSS.supports("height: 100svh")
	);
}

function _isMobile(): boolean {
	return (
		/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
		(navigator.userAgent.match(/Mac/) !== null && navigator.maxTouchPoints > 2)
	);
}

export default function ViewportHeightPolyfill() {
	useEffect(() => {
		// if (!isMobile()) return;
		// if (browserSupportsNewVhUnits()) return;

		updateViewport();
		const timeoutId = setTimeout(updateViewport, 100);

		const handleOrientationChange = () => setTimeout(updateViewport, 100);
		// Returning from an app-switch is the failure boundary: recompute viewport vars and, on Apple
		// devices, nudge WebKit to recompute a stuck layout viewport.
		const handleResume = () => {
			updateViewport();
			if (isAppleDevice()) reassertViewportMeta();
		};
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") handleResume();
		};

		window.addEventListener("resize", updateViewport);
		window.addEventListener("orientationchange", handleOrientationChange);
		// `pageshow` fires on return-from-background/BFCache where `resize`/`visibilitychange` can be
		// unreliable in WKWebView.
		window.addEventListener("pageshow", handleResume);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		if (window.visualViewport) {
			window.visualViewport.addEventListener("resize", updateViewport);
			window.visualViewport.addEventListener("scroll", updateViewport);
		}

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener("resize", updateViewport);
			window.removeEventListener("orientationchange", handleOrientationChange);
			window.removeEventListener("pageshow", handleResume);
			document.removeEventListener("visibilitychange", handleVisibilityChange);

			if (window.visualViewport) {
				window.visualViewport.removeEventListener("resize", updateViewport);
				window.visualViewport.removeEventListener("scroll", updateViewport);
			}

			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, []);

	return null;
}
